/**
 * <CollisionMonitor> — runs `checkSamePathCollisions` continuously inside
 * the r3f Canvas and fires a callback when the result set changes.
 *
 * Lives inside `<XtsViewer3D>`'s Canvas so it can read live positions
 * from the MoverPositionStore via `useFrame` — same per-frame contract
 * as the mover groups themselves, no React reconciliation per tick.
 *
 * Throttling: pass `intervalMs` to limit the check rate (e.g. 50 ms for
 * 20 Hz monitoring on slow hardware). Default 0 = every animation frame.
 *
 * Change detection: the callback fires only when the collision set
 * actually changes — pair appears, pair disappears, or
 * `penetrationMm` for an existing pair shifts by more than 0.01 mm.
 * This keeps a UI bound to `onCollisionsChange` from re-rendering 60×
 * per second when the scene is steady.
 */

import { useContext, useEffect, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import {
  buildChain,
  type BuiltChain,
} from '../../geometry/ChainBuilder.js';
import {
  checkSamePathCollisions,
  type CheckMoverCollisionsOptions,
  type MoverCollision,
  type MoverProbe,
} from '../../geometry/moverCollision.js';
import type { NormalizedXtsConfig } from '../../geometry/normalizeXtsConfig.js';
import { MoverPositionStoreContext } from './MoverPositionStore.js';

const PENETRATION_EPS_MM = 0.01;

interface CollisionMonitorProps {
  config: NormalizedXtsConfig;
  warningGapMm?: number;
  intervalMs?: number;
  forceClosedLoop?: boolean;
  onCollisionsChange: (collisions: MoverCollision[]) => void;
}

export const CollisionMonitor: React.FC<CollisionMonitorProps> = ({
  config,
  warningGapMm,
  intervalMs = 0,
  forceClosedLoop,
  onCollisionsChange,
}) => {
  const store = useContext(MoverPositionStoreContext);
  // Last-fired snapshot — keyed `idA|idB|penetrationFloor`. Used to
  // suppress callback when nothing meaningful changed since the last
  // tick.
  const lastKeyRef = useRef<string>('');
  const lastTickRef = useRef<number>(0);

  // Pre-build chains + per-part mover lists once per config change. The
  // collision math runs every frame but the topology only needs to be
  // walked again when the config (modules / part list) changes.
  const partsRef = useRef<
    Array<{ chain: BuiltChain; partOid: number; xpuObjectId: number; moverList: Array<{
      moverIndex: number;
      id: number;
      partOid: number;
      moverType: NormalizedXtsConfig['processingUnits'][number]['moverType'];
      partPositionMm: number;
      pathLengthOverrideMm?: number;
    }>; }>
  >([]);
  useEffect(() => {
    const out: typeof partsRef.current = [];
    for (const xpu of config.processingUnits) {
      for (const part of xpu.parts) {
        try {
          const chain = buildChain(part.modules);
          const moverList = xpu.movers
            .filter((m) => m.partOid === part.globalNumber)
            .map((m) => ({
              moverIndex: m.index,
              id: m.id,
              partOid: m.partOid,
              moverType: xpu.moverType,
              partPositionMm: m.partPositionMm,
              ...(xpu.moverType === 'Custom' &&
              xpu.customMoverLayout?.pathLengthMm !== undefined
                ? { pathLengthOverrideMm: xpu.customMoverLayout.pathLengthMm }
                : {}),
            }));
          if (moverList.length >= 2) {
            out.push({
              chain,
              partOid: part.globalNumber,
              xpuObjectId: xpu.objectId,
              moverList,
            });
          }
        } catch {
          // already validated by normalizeXtsConfig — defensive.
        }
      }
    }
    partsRef.current = out;
    lastKeyRef.current = ''; // force initial fire on next tick
  }, [config]);

  useFrame(({ clock }) => {
    if (intervalMs > 0) {
      const t = clock.getElapsedTime() * 1000;
      if (t - lastTickRef.current < intervalMs) return;
      lastTickRef.current = t;
    }

    const opts: CheckMoverCollisionsOptions = {};
    if (warningGapMm !== undefined) opts.warningGapMm = warningGapMm;
    if (forceClosedLoop !== undefined) opts.forceClosedLoop = forceClosedLoop;

    const all: MoverCollision[] = [];
    for (const part of partsRef.current) {
      const probes: MoverProbe[] = part.moverList.map((m) => {
        const live = store?.get(m.id);
        const probe: MoverProbe = {
          ref: { processingUnitObjectId: part.xpuObjectId, moverIndex: m.moverIndex },
          id: m.id,
          partOid: m.partOid,
          moverType: m.moverType,
          partPositionMm: live ?? m.partPositionMm,
        };
        if (m.pathLengthOverrideMm !== undefined) {
          probe.pathLengthMm = m.pathLengthOverrideMm;
        }
        return probe;
      });
      const out = checkSamePathCollisions(
        part.chain,
        part.partOid,
        probes,
        opts,
      );
      for (const c of out) all.push(c);
    }
    all.sort((a, b) => b.penetrationMm - a.penetrationMm);

    // Stable key — only triggers callback when the *meaningful* set
    // changes. Floor penetration to PENETRATION_EPS_MM so micro-jitter
    // from float math doesn't keep the callback hot.
    const key = all
      .map((c) => {
        const penFloor =
          Math.round(c.penetrationMm / PENETRATION_EPS_MM) *
          PENETRATION_EPS_MM;
        // Canonicalise pair order (smaller id first) so swapping
        // doesn't show up as a new pair.
        const [lo, hi] = c.idA < c.idB ? [c.idA, c.idB] : [c.idB, c.idA];
        return `${lo}|${hi}|${penFloor.toFixed(2)}`;
      })
      .join(';');

    if (key !== lastKeyRef.current) {
      lastKeyRef.current = key;
      onCollisionsChange(all);
    }
  });

  return null;
};
